import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/** The query param another surface (the AI assistant) sets to have a page
 * open one of its tools: `/job-search/<alias>?open=cover_letter`. */
export const OPEN_PARAM = 'open';

export const withOpen = (path: string, key?: string | null) =>
  key
    ? `${path}${path.includes('?') ? '&' : '?'}${OPEN_PARAM}=${encodeURIComponent(key)}`
    : path;

export const asksToOpen = (path: string) =>
  new URLSearchParams(path.split('?')[1] ?? '').has(OPEN_PARAM);

/**
 * Opens what the URL asks for when it is `key` (or one of `key`), once
 * `ready`: the ask is dropped from the URL first, so a refresh or Back doesn't
 * open it again.
 */
const useOpenFromUrl = <K extends string>(
  key: K | readonly K[],
  onOpen: (asked: K) => void,
  ready = true,
) => {
  const [params, setParams] = useSearchParams();
  const value = params.get(OPEN_PARAM) as K | null;
  const keys: readonly K[] = typeof key === 'string' ? [key] : key;
  const asked = value && keys.includes(value) ? value : null;
  const onOpenRef = useRef(onOpen);
  useEffect(() => {
    onOpenRef.current = onOpen;
  });

  useEffect(() => {
    if (!asked || !ready) return;
    setParams(
      (current) => {
        current.delete(OPEN_PARAM);
        return current;
      },
      { replace: true },
    );
    onOpenRef.current(asked);
  }, [asked, ready, setParams]);
};

export default useOpenFromUrl;
